/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useContext, useId, useEffect, useRef} from 'react'
import {useIntl} from 'react-intl'

// Components
import {Accordion, Box, Button, Flex, Text} from '@chakra-ui/react'
import StoresList from './stores-list'

// Others
import {
    STORE_LOCATOR_DISTANCE,
    STORE_LOCATOR_NUM_STORES_PER_LOAD,
    STORE_LOCATOR_DISTANCE_UNIT,
    STORE_LOCATOR_HREF
} from '../../constants'

//This is an API limit and is therefore not configurable
const NUM_STORES_PER_REQUEST_API_MAX = 200

// Hooks
import {useSearchStores} from '@salesforce/commerce-sdk-react'

import {StoreLocatorContext} from './index'
import FindStoreLink from '../find-store/find-store-link'
import {useLocation} from 'react-router-dom'
import {useGeocodeAddress, useGoogleMapsApiLoader} from '../../google-maps-api'
import {useCountry} from '../../hooks'
import useMultiSite from '../../hooks/use-multi-site'

const StoreLocatorContent = () => {
    const {searchStoresParams, setSearchStoresParams, setUserHasSetManualGeolocation} =
        useContext(StoreLocatorContext)
    const {countryCode, address, latitude, longitude, limit} = searchStoresParams
    const intl = useIntl()
    const {buildUrl} = useMultiSite()
    const [alreadySearched, setAlreadySearched] = useState(false)
    const location = useLocation()
    const currentCountryCode = useCountry()
    const {coords} = useGeocodeAddress(address, currentCountryCode)
    const getGoogleMapsAPI = useGoogleMapsApiLoader()

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const latitude = params.get('lat') ? Number(params.get('lat')) : null
        const longitude = params.get('lng') ? Number(params.get('lng')) : null
        const address = params.get('address')
        setSearchStoresParams({
            latitude,
            longitude,
            address,
            countryCode: currentCountryCode,
            limit: STORE_LOCATOR_NUM_STORES_PER_LOAD
        })
    }, [currentCountryCode, location.search, setSearchStoresParams])

    const coordsHandled = useRef(false)
    useEffect(() => {
        if (coords && !coordsHandled.current) {
            coordsHandled.current = true
            setSearchStoresParams({
                latitude: coords.latitude,
                longitude: coords.longitude,
                limit: STORE_LOCATOR_NUM_STORES_PER_LOAD
            })
        }
    }, [coords, setSearchStoresParams])

    const [numStoresToShow, setNumStoresToShow] = useState(limit)

    // Either the countryCode & postalCode or latitude & longitude are defined, never both
    // If coordinates are passed, the values for country and postal code are ignored.
    const {
        data: searchStoresData,
        isLoading,
        refetch,
        isFetching
    } = useSearchStores({
        parameters: {
            countryCode: countryCode,
            // postalCode: postalCode, we don't have the mapping?
            latitude: latitude,
            longitude: longitude,
            locale: intl.locale,
            maxDistance: STORE_LOCATOR_DISTANCE,
            limit: NUM_STORES_PER_REQUEST_API_MAX,
            distanceUnit: STORE_LOCATOR_DISTANCE_UNIT
        }
    })

    const storesInfo =
        isLoading || isFetching
            ? undefined
            : searchStoresData?.data?.slice(0, numStoresToShow) || []
    const numStores = searchStoresData?.total || 0

    const submitForm = async (formData) => {
        setAlreadySearched(true)
        const {address} = formData

        if (!!address && !formData.latitude && !formData.longitude) {
            const googleMapsAPI = (await getGoogleMapsAPI()) || {}

            if (!googleMapsAPI) {
                throw new Error('Google maps api unavailable.')
            }

            const location = await new Promise((resolve, reject) => {
                const geocoder = new googleMapsAPI.Geocoder()
                geocoder.geocode({address}, (results, status) => {
                    if (status === 'OK' && results?.[0]?.geometry?.location) {
                        resolve(results[0].geometry.location)
                    } else {
                        reject(new Error(status || 'Geocoding failed'))
                    }
                })
            })

            formData.latitude = location.lat()
            formData.longitude = location.lng()
            formData.address = address
        }

        setSearchStoresParams({
            latitude: formData.latitude,
            longitude: formData.longitude,
            address: formData.address,
            limit: STORE_LOCATOR_NUM_STORES_PER_LOAD
        })
        setUserHasSetManualGeolocation(true)
        setNumStoresToShow(STORE_LOCATOR_NUM_STORES_PER_LOAD)

        window.history.pushState(
            {},
            '',
            `${buildUrl(STORE_LOCATOR_HREF)}?${new URLSearchParams(formData)}`
        )

        // Ensures API call is made regardless of caching to provide UX feedback on click
        refetch()
    }

    const addressFieldId = useId()

    return (
        <>
            <FindStoreLink addressFieldId={addressFieldId} onSubmit={submitForm} />
            <Flex pt="4">
                <Box width="100%">
                    <Accordion allowMultiple flex={[1, 1, 1, 5]}>
                        {storesInfo?.length > 0 ? (
                            <StoresList
                                storesInfo={storesInfo?.map((store) => ({
                                    ...store,
                                    infoWindowHtml: `<strong>${store?.name ?? ''}</strong><p>${
                                        store?.address1 ?? ''
                                    } ${store?.city ?? ''} ${store?.stateCode ?? ''}</p>`
                                }))}
                            />
                        ) : (
                            <>
                                {alreadySearched && (
                                    <Text>
                                        {intl.formatMessage({
                                            defaultMessage:
                                                'Sorry, there are no locations in this area',
                                            id: 'store_locator.description.no_locations'
                                        })}
                                    </Text>
                                )}
                            </>
                        )}
                    </Accordion>
                    {!isFetching &&
                        numStoresToShow < numStores &&
                        numStoresToShow < NUM_STORES_PER_REQUEST_API_MAX && (
                            <Box paddingTop="10px" marginTop="10px">
                                <Button
                                    key="load-more-button"
                                    onClick={() => {
                                        setNumStoresToShow(
                                            numStoresToShow + STORE_LOCATOR_NUM_STORES_PER_LOAD <=
                                                NUM_STORES_PER_REQUEST_API_MAX
                                                ? numStoresToShow +
                                                      STORE_LOCATOR_NUM_STORES_PER_LOAD
                                                : numStoresToShow
                                        )
                                    }}
                                    width="100%"
                                    variant="outline"
                                    marginBottom={4}
                                >
                                    {intl.formatMessage({
                                        id: 'global.loadmore',
                                        defaultMessage: 'Load more'
                                    })}
                                </Button>
                            </Box>
                        )}
                </Box>
            </Flex>
        </>
    )
}

StoreLocatorContent.propTypes = {}

export default StoreLocatorContent
